import * as React from 'react';
import { useEffect, useState } from 'react';
import {
    BooleanField,
    BooleanInput,
    Create,
    CreateButton,
    Datagrid,
    Edit,
    FunctionField,
    List,
    NumberInput,
    SaveButton,
    SelectInput,
    SimpleForm,
    TextField,
    TextInput,
    Toolbar,
    maxLength,
    minValue,
    number,
    regex,
    required,
    useGetList,
    useNotify,
    useRecordContext,
    useRedirect,
} from 'react-admin';
import { useFormContext, useWatch } from 'react-hook-form';
import { Box, Button, Card, CardContent, Stack, Table, TableBody, TableCell, TableHead, TableRow, Typography } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { useTranslation } from 'react-i18next';
import DealService, { type Deal, type DealClaim, type MenuDish } from '../../services/deal/dealService.ts';
import { errorCode, errorMessage, euro, fmtDateTime } from './marketingShared.ts';

/** The react-admin resource; the data provider sends it to /admin/deals as is. */
export const DEALS_RESOURCE = 'admin/deals';

/** The fields the server accepts on POST and PUT (SaveDealRequest). */
const EDITABLE = [
    'code', 'name', 'locationId', 'inventoryItemId', 'dealPrice', 'restaurantShare',
    'startTime', 'endTime', 'waiveMinOrder', 'isActive', 'notes',
] as const;

const toBody = (data: any) => {
    const body: Record<string, unknown> = {};
    EDITABLE.forEach((key) => { body[key] = data[key] ?? null; });
    body.code = String(data.code ?? '').trim().toUpperCase();
    body.waiveMinOrder = data.waiveMinOrder !== false;
    body.isActive = data.isActive !== false;
    return body;
};

const HH_MM = /^([01]\d|2[0-3]):[0-5]\d$/;

const ListActions = () => {
    const { t } = useTranslation();
    return (
        <Box sx={{ pt: 4, pb: 2, flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Typography variant="h5" sx={{ fontWeight: 700, fontSize: 'var(--text-xxl)' }}>
                {t('resources.deals')}
            </Typography>
            <CreateButton icon={<AddIcon />} label={t('deals.add').toLocaleUpperCase()}
                sx={{ color: 'var(--color-primary)', fontSize: '16px', fontWeight: 700 }} />
        </Box>
    );
};

export const DealsList = () => {
    const { t } = useTranslation();
    return (
        <List actions={<ListActions />} perPage={25} title={t('resources.deals')} sx={{ '& .RaList-main': { px: 4 } }}>
            <Datagrid bulkActionButtons={false} rowClick="edit"
                sx={{ '& .RaDatagrid-headerCell': { fontWeight: 700 }, '& td, & th': { fontSize: 'var(--text-xs)' } }}>
                <TextField source="code" label={t('deals.code')} sortable={false} />
                <TextField source="restaurantName" label={t('deals.restaurant')} sortable={false} />
                <TextField source="itemName" label={t('deals.dish')} sortable={false} />
                <FunctionField source="dealPrice" label={t('deals.price')} sortable={false} textAlign="right"
                    render={(r: Deal) => `${euro(r?.dealPrice)} (${euro(r?.menuPrice)})`} />
                <FunctionField source="restaurantShare" label={t('deals.split')} sortable={false} textAlign="right"
                    render={(r: Deal) => `${euro(r?.restaurantShare)} / ${euro(r?.velloShare)}`} />
                <FunctionField source="startTime" label={t('deals.hours')} sortable={false}
                    render={(r: Deal) => `${r?.startTime ?? ''}–${r?.endTime ?? ''}`} />
                <BooleanField source="isActive" label={t('deals.active')} sortable={false} />
                <FunctionField source="claims" label={t('deals.claims')} sortable={false} textAlign="right" render={(r: Deal) => r?.claims ?? 0} />
                <FunctionField source="used" label={t('deals.used')} sortable={false} textAlign="right" render={(r: Deal) => r?.used ?? 0} />
            </Datagrid>
        </List>
    );
};

/** Restaurant, then one of its dishes; the menu price and the split follow the dish. */
const RestaurantAndDish: React.FC<{ creating?: boolean }> = ({ creating }) => {
    const { t } = useTranslation();
    const { setValue } = useFormContext();
    const locationId = useWatch({ name: 'locationId' });
    const inventoryItemId = useWatch({ name: 'inventoryItemId' });
    const dealPrice = useWatch({ name: 'dealPrice' });
    const restaurantShare = useWatch({ name: 'restaurantShare' });
    const { data: restaurants } = useGetList('locationsManagement', { pagination: { page: 1, perPage: 500 }, sort: { field: 'name', order: 'ASC' } });
    const [dishes, setDishes] = useState<MenuDish[]>([]);
    useEffect(() => {
        let cancelled = false;
        setDishes([]);
        if (!locationId) return undefined;
        new DealService().dishes(Number(locationId)).then((rows) => { if (!cancelled) setDishes(rows); }).catch(() => { if (!cancelled) setDishes([]); });
        return () => { cancelled = true; };
    }, [locationId]);
    const dish = dishes.find((d) => d.id === Number(inventoryItemId));
    const menuPrice = dish ? Number(String(dish.price).replace(/[^0-9.]/g, '')) : null;
    const discount = menuPrice != null && dealPrice != null ? Math.max(0, menuPrice - Number(dealPrice)) : null;
    const vello = discount != null ? Math.max(0, discount - Math.min(discount, Number(restaurantShare ?? 0))) : null;
    return (
        <>
            <SelectInput
                source="locationId"
                label={t('deals.restaurant')}
                choices={(restaurants ?? []).map((r: any) => ({ id: r.id, name: r.name }))}
                validate={required()}
                onChange={() => setValue('inventoryItemId', null)}
                disabled={!creating}
                helperText={creating ? undefined : t('deals.restaurantFixed')}
                fullWidth
            />
            <SelectInput
                source="inventoryItemId"
                label={t('deals.dish')}
                choices={dishes.map((d) => ({ id: d.id, name: `${d.name} (${d.price})` }))}
                validate={required()}
                disabled={!locationId || dishes.length === 0}
                helperText={!locationId ? t('deals.pickRestaurantFirst') : undefined}
                fullWidth
            />
            <NumberInput source="dealPrice" label={t('deals.dealPrice')} validate={[required(), number(), minValue(0)]} min={0} step={0.5} sx={{ mt: 2 }}
                helperText={menuPrice != null ? t('deals.menuPriceIs', { price: euro(menuPrice) }) : undefined} />
            <NumberInput source="restaurantShare" label={t('deals.restaurantShare')} validate={[required(), number(), minValue(0)]} min={0} step={0.5} sx={{ mt: 2 }}
                helperText={discount != null
                    ? t('deals.splitHelp', { discount: euro(discount), restaurant: euro(Math.min(discount, Number(restaurantShare ?? 0))), vello: euro(vello) })
                    : t('deals.splitHelpPlain')} />
        </>
    );
};

const DealForm: React.FC<{ creating?: boolean }> = ({ creating }) => {
    const { t } = useTranslation();
    return (
        // Save only: a deal is never deleted (its claims and orders refer to
        // it); "Active" off pauses it.
        <SimpleForm sx={{ maxWidth: 720 }} toolbar={<Toolbar><SaveButton /></Toolbar>}>
            <TextInput
                source="code"
                label={t('deals.code')}
                helperText={creating ? t('deals.codeHelp') : t('deals.codeChangeHelp')}
                parse={(v: string) => (v ?? '').toUpperCase()}
                validate={[required(), regex(/^[A-Za-z0-9-]{2,32}$/, t('deals.codeInvalid'))]}
                fullWidth
            />
            <TextInput source="name" label={t('deals.name')} helperText={t('deals.nameHelp')} validate={[required(), maxLength(200)]} fullWidth />
            <RestaurantAndDish creating={creating} />
            <Stack direction="row" spacing={2} sx={{ mt: 2 }}>
                <TextInput source="startTime" label={t('deals.from')} validate={[required(), regex(HH_MM, t('deals.timeInvalid'))]} placeholder="12:00" />
                <TextInput source="endTime" label={t('deals.to')} validate={[required(), regex(HH_MM, t('deals.timeInvalid'))]} placeholder="21:00" />
            </Stack>
            <Typography variant="caption" color="text.secondary" sx={{ mt: -2, mb: 2 }}>{t('deals.hoursHelp')}</Typography>
            <BooleanInput source="waiveMinOrder" label={t('deals.waiveMinOrder')} helperText={t('deals.waiveMinOrderHelp')} />
            <TextInput source="notes" label={t('deals.notes')} multiline minRows={2} fullWidth />
            <BooleanInput source="isActive" label={t('deals.active')} helperText={t('deals.activeHelp')} />
        </SimpleForm>
    );
};

/** A 409 on a taken code gets its own words; anything else shows what the server said. */
const useSaveError = () => {
    const notify = useNotify();
    const { t } = useTranslation();
    return (e: any) => {
        if (errorCode(e) === 'error.deal_code_taken') {
            notify(t('deals.codeTaken'), { type: 'error' });
        } else {
            notify(errorMessage(e, e?.message || t('common.saveFailed')), { type: 'error' });
        }
    };
};

export const DealCreate = () => {
    const { t } = useTranslation();
    const onError = useSaveError();
    const notify = useNotify();
    const redirect = useRedirect();
    return (
        <Create
            title={t('deals.add')}
            transform={toBody}
            mutationOptions={{
                onError,
                onSuccess: (data: any) => {
                    notify(t('common.saved'), { type: 'success' });
                    redirect('edit', DEALS_RESOURCE, data.id);
                },
            }}
            record={{ dealPrice: 0, restaurantShare: 0, startTime: '12:00', endTime: '21:00', waiveMinOrder: true, isActive: true }}
        >
            <DealForm creating />
        </Create>
    );
};

/** The link to share, and who opened and used it. */
const DealClaimsPanel = () => {
    const record = useRecordContext<Deal>();
    const { t } = useTranslation();
    const notify = useNotify();
    const [claims, setClaims] = useState<DealClaim[] | null>(null);
    const [failed, setFailed] = useState<string | null>(null);
    useEffect(() => {
        if (!record?.id) return undefined;
        let cancelled = false;
        new DealService().claims(record.id)
            .then((rows) => { if (!cancelled) setClaims(rows); })
            .catch((e) => { if (!cancelled) setFailed(errorMessage(e, t('deals.claimsFailed'))); });
        return () => { cancelled = true; };
    }, [record?.id]);
    if (!record?.id) return null;
    const copy = async () => {
        try {
            await navigator.clipboard.writeText(record.link);
            notify(t('deals.linkCopied'), { type: 'success' });
        } catch (e) {
            notify(record.link, { type: 'info' });
        }
    };
    return (
        <Card sx={{ mx: 2, mb: 3, maxWidth: 900 }}>
            <CardContent>
                <Typography variant="h6" sx={{ fontWeight: 700 }}>{t('deals.link')}</Typography>
                <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 1, mb: 2 }}>
                    <Typography sx={{ fontFamily: 'monospace' }} data-testid="deal-link">{record.link}</Typography>
                    <Button size="small" startIcon={<ContentCopyIcon />} onClick={copy}>{t('deals.copyLink')}</Button>
                </Stack>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>{t('deals.linkHelp')}</Typography>
                <Typography variant="h6" sx={{ fontWeight: 700 }}>
                    {t('deals.claimsTitle', { claims: record.claims ?? 0, used: record.used ?? 0 })}
                </Typography>
                {failed && <Typography color="error">{failed}</Typography>}
                {claims && claims.length === 0 && <Typography color="text.secondary">{t('deals.noClaims')}</Typography>}
                {claims && claims.length > 0 && (
                    <Table size="small" data-testid="deal-claims">
                        <TableHead>
                            <TableRow>
                                <TableCell>{t('deals.customer')}</TableCell>
                                <TableCell>{t('deals.phone')}</TableCell>
                                <TableCell>{t('deals.claimedAt')}</TableCell>
                                <TableCell>{t('deals.usedAt')}</TableCell>
                                <TableCell>{t('deals.order')}</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {claims.map((c) => (
                                <TableRow key={c.id}>
                                    <TableCell>{c.customerName || `#${c.customerId}`}</TableCell>
                                    <TableCell sx={{ direction: 'ltr' }}>{c.phoneNumber || '—'}</TableCell>
                                    <TableCell>{fmtDateTime(c.claimedAt)}</TableCell>
                                    <TableCell>{c.usedAt ? fmtDateTime(c.usedAt) : '—'}</TableCell>
                                    <TableCell>{c.orderNumber || '—'}</TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                )}
            </CardContent>
        </Card>
    );
};

export const DealEdit = () => {
    const { t } = useTranslation();
    const onError = useSaveError();
    return (
        <Edit title={t('resources.deals')} transform={toBody} mutationMode="pessimistic" mutationOptions={{ onError }}>
            <DealForm />
            <DealClaimsPanel />
        </Edit>
    );
};
